import React, { useMemo } from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { Trans, useTranslation } from 'react-i18next';
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import {
  openMoneygramPrivacyPolicy,
  openMoneygramPrivacyPolicyUsEs,
  openMoneygramTermsAndConditions,
  openMoneygramTermsAndConditionsUsEs,
} from "../../../../../helper/moneygramLegalLinks";
import EnglishFlag from "../../../../../../../../assets/images/EnglishFlag.svg";
import DownArrow from "../../../../../../../../assets/images/DownArrowIcon.svg";
import Colors from "../../../../../constants/colors";
import { formatBillerDisplayName } from "../../../../../helper/billerDisplayName";

interface ReviewFinalStepProps {
  onContinue?: () => void;
  onEditStep?: (key: string) => void;
  testID?: string;
}

const BillPayFinalReviewStep: React.FC<ReviewFinalStepProps> = ({ onEditStep, onContinue, testID }) => {
  const { t, i18n } = useTranslation();
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const currentSendAmount = useSessionStore((s: SessionState) => (s as any).currentSendAmount);
  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.selectedDestinationStateAbbr);
  const selectedReceiveCurrency = useSessionStore((s: SessionState) => s.selectedReceiveCurrency);
  const receiverFirstName = useSessionStore((s: SessionState) => (s as any).receiverFirstName);
  const receiverMiddleName = useSessionStore((s: SessionState) => (s as any).receiverMiddleName);
  const receiverLastName = useSessionStore((s: SessionState) => (s as any).receiverLastName);
  const receiverLastName2 = useSessionStore((s: SessionState) => (s as any).receiverLastName2);
  const partnerAddress = useSessionStore((s: SessionState) => (s as any).partnerAddress);
  const senderFirstName = useSessionStore((s: SessionState) => (s as any).senderFirstName);
  const senderMiddleName = useSessionStore((s: SessionState) => (s as any).senderMiddleName);
  const senderLastName = useSessionStore((s: SessionState) => (s as any).senderLastName);
  const senderLastName2 = useSessionStore((s: SessionState) => (s as any).senderLastName2);
  const senderAddress = useSessionStore((s: SessionState) => (s as any).senderAddress);
  const senderCity = useSessionStore((s: SessionState) => (s as any).senderCity);
  const senderZipCode = useSessionStore((s: SessionState) => (s as any).senderZipCode);
  const senderState = useSessionStore((s: SessionState) => (s as any).senderState);
  const senderDOB = useSessionStore((s: SessionState) => (s as any).senderDOB);
  const senderDOBString = useSessionStore((s: SessionState) => (s as any).senderDOBString);
  const phoneNumber = useSessionStore((s: SessionState) => (s as any).phoneNumber);
  const customerReceiveNumber = useSessionStore((s: SessionState) => (s as any).customerReceiveNumber);
  const senderPhotoIdNumber = useSessionStore((s: SessionState) => (s as any).senderPhotoIdNumber);
  // fetch additional information dynamically
  const additionalFieldValues = useSessionStore((s: SessionState) => (s as any).additionalFieldValues) as Record<string, any>;
  const billPayBillerAccountNumberStore = useSessionStore((s: SessionState) => (s as any).billPayBillerAccountNumberStore);
  const billPayBillerSearchResponseStore = useSessionStore((s: SessionState) => (s as any).billPayBillerSearchResponseStore);
  const billPayFeeLookupResponseStore = useSessionStore((s: SessionState) => (s as any).billPayFeeLookupResponseStore);

  const selectedBillerInformation = useSessionStore((s: SessionState) => (s as any).selectedBillerInformationStore);

  const maskedAccountNumber = useMemo(() => {
    const raw = String(billPayBillerAccountNumberStore || '');
    if (!raw) return '';
    const last4 = raw.slice(-4);
    const maskLength = Math.max(0, raw.length - 4);
    return `${'*'.repeat(maskLength)}${last4}`;
  }, [billPayBillerAccountNumberStore]);

  const option: any = pendingSendOption || selectedSendOption || null;

  const summary = useMemo(() => {
    const sendCurrency: string = option?.SendAmounts?.SendCurrency || "USD";
    const amountFromState = Number(String(currentSendAmount || "0").replace(/,/g, ""));
    const amountFromOption = Number(option?.SendAmounts?.SendAmount ?? 0);
    const sendAmountNum: number = isFinite(amountFromOption) && amountFromOption > 0
      ? amountFromOption
      : (isFinite(amountFromState) ? amountFromState : 0);
    const receiveCurrency: string = selectedReceiveCurrency || (option ? FeeLookupHelper.getReceiveCurrency(option) : "");
    const receiveAmount: number = option ? FeeLookupHelper.getReceiveAmount(option) : 0;
    const exchangeRateNum: number = option ? FeeLookupHelper.getExchangeRate(option) : 0;
    const fees: number = Number(option?.SendAmounts?.TotalDiscountAmount || 0) + Number(option?.SendAmounts?.TotalSendFees || 0);
    const total: number = Number(option?.TotalAmount || (sendAmountNum + fees));

    return {
      sendAmount: sendAmountNum,
      sendCurrency,
      receiveAmount,
      receiveCurrency,
      exchangeRate: exchangeRateNum,
      fees,
      total,
    };
  }, [option, currentSendAmount, selectedReceiveCurrency]);

  const receiverName = useMemo(() => {
    return [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2]
      .filter(Boolean)
      .join(" ");
  }, [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2]);

  const senderName = useMemo(() => {
    return [senderFirstName, senderMiddleName, senderLastName, senderLastName2]
      .filter(Boolean)
      .join(" ");
  }, [senderFirstName, senderMiddleName, senderLastName, senderLastName2]);

  const senderAddressFull = useMemo(() => {
    const parts = [
      senderAddress,
      [senderCity, senderState, senderZipCode].filter(Boolean).join(", ")
    ].filter(Boolean);
    return parts.join("\n");
  }, [senderAddress, senderCity, senderState, selectedDestinationStateAbbr, senderZipCode]);

  const idNumberToShow = customerReceiveNumber || senderPhotoIdNumber || "";

  const dobToShow = useMemo(() => {
    if (senderDOBString) return senderDOBString;
    if (senderDOB instanceof Date && !isNaN((senderDOB as any))) {
      const mm = String(senderDOB.getMonth() + 1).padStart(2, "0");
      const dd = String(senderDOB.getDate()).padStart(2, "0");
      const yyyy = senderDOB.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }
    return "";
  }, [senderDOBString, senderDOB]);

  const additionalInfo = useMemo(() => {
    return Object.values(additionalFieldValues).filter(Boolean).join(', ');
  }, [additionalFieldValues]);

  const availableNotes = useMemo(() => {
    try {
      const raw: any = billPayBillerSearchResponseStore as any;
      const container: any = (raw?.BillerInfo ?? raw?.billerInfo ?? raw) as any;
      const item: any = (container?.BillerInfos?.[0] ?? container?.billerInfos?.[0] ?? container) as any;
      const notes: string = String(item?.BillerNotes ?? item?.billerNotes ?? '').trim();
      return notes;
    } catch {
      return '';
    }
  }, [billPayBillerSearchResponseStore]);

  const totalsFromFeeLookup = useMemo(() => {
    try {
      const raw: any = billPayFeeLookupResponseStore as any;

       // Normalize inputs that may be JSON strings
       const normalizedFeeLookup: any = (() => {
        let raw: any = billPayFeeLookupResponseStore as any;
        if (typeof raw === 'string') {
          try { raw = JSON.parse(raw); } catch {}
        }
        return raw;
      })();

      const totalFeesRaw: any = normalizedFeeLookup?.FeeInfos[0].SendAmounts.TotalSendFees
      ?? 0;
      const totalAmountRaw: any = normalizedFeeLookup?.FeeInfos[0].TotalAmount?? 0;
      const parseNum = (v: any) => {
        const s = String(v ?? '0');
        const n = Number(s.replace(/,/g, ''));
        return isFinite(n) ? n : 0;
      };
      return {
        totalFees: parseNum(totalFeesRaw),
        totalAmount: parseNum(totalAmountRaw),
      };
    } catch {
      return { totalFees: 0, totalAmount: 0 };
    }
  }, [billPayFeeLookupResponseStore]);

  const termsAgreementLinkComponents = useMemo(
    () => ({
      termsLink: (
        <Text
          style={[styles.link, { textDecorationLine: "underline" }]}
          onPress={
            i18n.language.startsWith("es")
              ? openMoneygramTermsAndConditionsUsEs
              : openMoneygramTermsAndConditions
          }
          accessibilityRole="link"
        />
      ),
      privacyLink: (
        <Text
          style={[styles.link, { textDecorationLine: "underline" }]}
          onPress={
            i18n.language.startsWith("es")
              ? openMoneygramPrivacyPolicyUsEs
              : openMoneygramPrivacyPolicy
          }
          accessibilityRole="link"
        />
      ),
    }),
    [i18n.language]
  );

  const exchangeNoticeComponents = useMemo(
    () => ({
      bold: <Text style={styles.bold} />,
    }),
    []
  );


  const data = {
    sendAmount: summary.sendAmount.toFixed(2),
    sendCurrency: summary.sendCurrency,
    receiveAmount: summary.receiveAmount.toFixed(2),
    receiveCurrency: summary.receiveCurrency || "—",
    receiverName: receiverName || "",
    destinationCountry: selectedDestinationCountryCode || "",
    destinationState: selectedDestinationStateAbbr || "",
    receiveMethod: option?.DeliveryOption || "",
    senderName: senderName || "",
    senderAddress: senderAddressFull || "",
    phoneNumber: phoneNumber || "",
    idNumber: idNumberToShow || "",
    dob: dobToShow || "",
    exchangeRate: summary.exchangeRate ? summary.exchangeRate.toFixed(4) : "0.0000",
    available: "Instantly",
    fees: summary.fees.toFixed(2),
    total: summary.total.toFixed(2),
    additionalInformation: additionalInfo || "",
  };
  return (
    <View style={styles.container} testID={testID}>
        <Text style={styles.title}>{t("REVIEW_TITLE")}</Text>
        <View>
          <Text style={styles.inputLabel}>{t("CVS_STORE_LOCATION")}</Text>
          <View style={styles.amountInput}> 
            {/* {locationName}, */}
            <Text style={styles.pickUpText}>{partnerAddress}</Text>
          </View>
        </View>
        <View style={[styles.sectionView, { marginTop: 24 }]}>
          <View>
            <Text style={styles.inputLabel}>{t("YOU_SEND")}</Text>
            <View style={styles.amountInput}>
              <Text style={styles.amountText}>{data.sendAmount}</Text>
              <Text style={styles.currencyText}>{data.sendCurrency}</Text>
            </View>
          </View>
          <TouchableOpacity testID="sendingAmountEntryButton"
            style={styles.editButton}
            onPress={() => onEditStep?.('payAmount')}>
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.sectionView}>
          <View style={styles.textContainer}>
            <Text style={[styles.inputLabel]}>{t("DESTINATION")}</Text>
            <View style={[styles.amountInput, { marginTop: 2, alignItems: 'center'}]}>
              <Text style={[styles.CountryText, styles.reviewValueMultiline]}>
                {formatBillerDisplayName(
                  String(
                    selectedBillerInformation?.BillerName ??
                      selectedBillerInformation?.billerName ??
                      ""
                  )
                )}
              </Text>
            </View>
          </View>
          <TouchableOpacity testID="countryButton"
            style={styles.editButton}
            onPress={() => onEditStep?.('previosBillerInfo')}>
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      { (selectedDestinationCountryCode === 'USA' || selectedDestinationCountryCode === 'CAN') &&
      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("DESTINATION_STATE")}</Text>
          <View style={[styles.amountInput, { marginTop: 2, alignItems: 'center'}]}>
            <EnglishFlag width={20} height={20}/>
            <Text style={[styles.CountryText, styles.reviewValueMultiline, { flex: 1 }]}>{data.destinationState}</Text>
          </View>
        </View>
        <TouchableOpacity testID="stateButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('state')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      }
      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("ACCOUNT_NUMBER")}</Text>
          <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{maskedAccountNumber}</Text>
        </View>
        <TouchableOpacity testID="accountInformationButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('accountInformation')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("SENDER_NAME")}</Text>
          <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{data.senderName}</Text>
        </View>
        <TouchableOpacity testID="senderNameButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('billPayFullName')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("ADDRESS")}</Text>
          <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{data.senderAddress}</Text>
        </View>
        <TouchableOpacity testID="senderAddressButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('billPayAddress')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("DOB")}</Text>
          <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{data.dob}</Text>
        </View>
        <TouchableOpacity testID="senderDOBButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('billPayDOB')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      { Object.keys(additionalFieldValues).length !== 0 &&
        <View style={styles.sectionView}>
          <View style={styles.textContainer}>
            <Text style={[styles.inputLabel]}>{t("ADDITIONAL_INFORMATION")}</Text>
            <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{data.additionalInformation}</Text>
          </View>
          <TouchableOpacity testID="additionalInformationButton"
            style={styles.editButton}
            onPress={() => onEditStep?.('additionalInformation')}>
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      }
        {/* <RowItem
          label="DOB"
          value={data.dob}
          editable
          onEditStep={() => onEditStep("dob")}
        /> */}

        {/* Terms */}
      <View style={{ marginVertical: 24, marginBottom: 16  }}>
        <View>
          <Text style={styles.terms}>
            <Trans
              i18nKey="BILL_PAY_FINAL_TERMS_AGREEMENT"
              components={termsAgreementLinkComponents}
            />
          </Text>
          <Text style={[styles.terms, { marginTop: 16 }]}>
            <Trans
              i18nKey="BILL_PAY_FINAL_EXCHANGE_NOTICE"
              components={exchangeNoticeComponents}
            />
          </Text>
        </View>
      </View>
      {/* <View><View style={styles.viewTap}/></View> */}
      {/* <GradientBackground style={styles.gradientCard}>
        <View style={styles.Crow}>
          <Text style={styles.feeLabel}>{t("AVAILABLE")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{availableNotes || ' '}</Text>
          </View>
        </View>
        <View style={styles.Crow}>
          <Text style={styles.feeLabel}>{t("FEES")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{totalsFromFeeLookup.totalFees.toFixed(2)} {data.sendCurrency}</Text>
          </View>
        </View>
        <View style={styles.Crow}>
          <Text style={styles.feeLabel}>{t("REVIEW_TOTAL")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{totalsFromFeeLookup.totalAmount.toFixed(2)} {data?.sendCurrency}</Text>
          </View>
        </View>
        <TouchableOpacity testID="continueButton"
          onPress={onContinue}
          activeOpacity={0.8}
          style={styles.continueButton}
        >
          <Text style={styles.continueButtonText}>{t("STRKEY_CONTINUE")}</Text>
        </TouchableOpacity>
      </GradientBackground> */}
    </View>
  );
};

const RowItem = ({
  label,
  value,
  editable,
  onEditStep,
}: {
  label: string;
  value: string;
  editable?: boolean;
  onEditStep?: () => void;
}) => (
  <View style={styles.row}>
    <View>
      <Text style={styles.rowLabel}>{label}</Text>
      <Text style={styles.rowValue}>{value}</Text>
    </View>
    {editable && (
      <TouchableOpacity onPress={onEditStep} testID="editButton">
        <Text style={styles.editBtn}>Edit</Text>
      </TouchableOpacity>
    )}
  </View>
);

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
    justifyContent: "flex-start",
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '400',
    fontFamily: 'Rubik',
    color: Colors.subTitleColor,
    lineHeight: 22,
    alignSelf: 'stretch'
  },
  amountInput: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  pickUpText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: Colors.titleTextColor,
    lineHeight: 24
  },
  sectionView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 32,
  },
  textContainer: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    marginRight: 8,
  },
  reviewValueMultiline: {
    flexShrink: 1,
    alignSelf: 'stretch',
  },
  amountText: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: '500',
    color: '#1f1f1f',
    lineHeight: 38
  },
  currencyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '500',
    color: '#8C8C8C',
    lineHeight: 18,
    paddingLeft: 6
  },
  editButton: {
    flexShrink: 0,
    backgroundColor: Colors.searchBackground,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 14
  },
  editText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '500',
    color: Colors.modalTitle,
    lineHeight: 16
  },
  CountryText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: '#1F1F1F',
    lineHeight: 24,
    // paddingLeft: 6
  },
  headerRow: { flexDirection: "row", alignItems: "center", marginBottom: 16 },
  progressBarContainer: {
    flex: 1,
    height: 4,
    backgroundColor: "#eee",
    marginLeft: 16,
    borderRadius: 2,
  },
  progressBar: { width: "50%", height: 4, backgroundColor: "#000" },
  title: {
    fontSize: 28,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 34,
    color: Colors.titleTextColor,
    marginBottom: 24
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
    alignItems: "center",
  },
  rowLabel: { fontSize: 14, color: "#555" },
  rowValue: { fontSize: 16, fontWeight: "600", marginTop: 2 },
  editBtn: { color: "#000", fontWeight: "500" },
  terms: {
    fontSize: 12,
    color: Colors.subTitleColor,
    fontFamily: 'Rubik',
    fontWeight: '400',
    lineHeight: 16,
  },
  link: {
    color: Colors.titleTextColor,
    fontWeight: "500",
  },
  bold: { fontWeight: "600" },
  bottomCard: {
    borderTopWidth: 1,
    borderColor: "#eee",
    padding: 16,
    backgroundColor: "#fafafa",
  },
  total: { marginTop: 8, fontSize: 16, fontWeight: "700" },
  continueBtn: {
    marginTop: 16,
    backgroundColor: "#000",
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  continueText: { color: "#fff", fontSize: 16, fontWeight: "600" },
  card: {
    alignItems: "center",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  gradientCard: {
    alignItems: "center",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    position: "absolute",
    right: 0,
    left: 0,
    top: "100%",
    transform: [{ translateX: -10 }],
  },
  viewTap: {
    height: 4,
    width: 77,
    backgroundColor: Colors.titleTextColor,
    borderRadius:24,
    marginBottom: 24,
    alignSelf: 'center'
  },
  Crow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 16,
  },
  feeLabel: {
    fontSize: 16,
    fontWeight: "500",
  },
  oldPrice: {
    fontSize: 16,
    // textDecorationLine: "line-through",
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
  button: {
    backgroundColor: "black",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 24,
    // marginHorizontal: 30,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  continueButton: {
    alignSelf: "center",
    backgroundColor: "#000",
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: "center",
    shadowColor: "#000",
  },
  continueButtonText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: "#FFF",
    lineHeight: 24
  },
});

export default BillPayFinalReviewStep;